import * as React from "react";

import _ from "lodash";
import { ChevronsUpDown, PlusIcon, XIcon } from "lucide-react";
import { Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const POPOVER_WIDTH = "w-[400px]";

interface RemoteSelectProps<T> {
  accessorFn: (option: T) =>
    | {
        id: string;
        name: string;
        iconUrl?: string;
        number?: string;
      }
    | undefined;
  onSearch: (
    query: string,
    onStatusChanged: (status: "idle" | "loading" | "error" | "success") => void,
    offset?: number,
  ) => Promise<T[]>;
  label?: string;
  value?: T;
  onValueChanged: (value: T | undefined) => void;
  disabled?: boolean;
  parentClassName?: string;
  className?: string;
  showSelected?: boolean;
  onCreate?: (query: string) => T;
  triggerType?: "keyboard" | "change";
  defaultOpen?: boolean;
  /**
   * When true, results are loaded incrementally: reaching the bottom of the
   * results list triggers another `onSearch` call with an increasing `offset`.
   * Loading stops once a page comes back empty.
   */
  paginated?: boolean;
  /** Put on the trigger button, so an external `<label htmlFor>` can point at it. */
  triggerId?: string;
  /** Load the first results when the list opens, with an empty query: for a short list that can be browsed. */
  loadOnOpen?: boolean;
  /** What the trigger shows while nothing is picked. */
  placeholder?: string;
  /** Id of the element naming this control — role="combobox" takes no name from its content. */
  ariaLabelledBy?: string;
}

export function RemoteSelect<T>({
  accessorFn,
  onSearch,
  label,
  value,
  onValueChanged,
  disabled = false,
  parentClassName,
  className,
  showSelected = false,
  onCreate,
  triggerType = "keyboard",
  defaultOpen = false,
  paginated = false,
  loadOnOpen = false,
  placeholder = "Select...",
  triggerId,
  ariaLabelledBy,
}: RemoteSelectProps<T>) {
  const [open, setOpen] = React.useState(defaultOpen);

  // Not memoised: a pick must call the `onValueChanged` of the latest render, not the first one's.
  const handleSetActive = (option: T | undefined) => {
    onValueChanged(option);

    // OPTIONAL: close the combobox upon selection
    setOpen(false);
  };

  return (
    <div
      className={cn("flex flex-col space-y-2 text-foreground", parentClassName)}
    >
      {label && <div className="text-sm font-medium">{label}</div>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            id={triggerId}
            aria-labelledby={ariaLabelledBy}
            className={cn("justify-between", className)}
            disabled={disabled}
          >
            {value ? accessorFn(value)?.name : placeholder}

            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent side="bottom" className={cn("p-0", POPOVER_WIDTH)}>
          <Search<T>
            selectedResult={value}
            onSelectResult={handleSetActive}
            onSearch={onSearch}
            accessorFn={accessorFn}
            showSelected={showSelected}
            onCreate={onCreate}
            triggerType={triggerType}
            paginated={paginated}
            loadOnOpen={loadOnOpen}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

interface SearchProps<T> {
  selectedResult?: T;
  onSelectResult: (value: T | undefined) => void;
  onSearch: RemoteSelectProps<T>["onSearch"];
  accessorFn: RemoteSelectProps<T>["accessorFn"];
  showSelected: RemoteSelectProps<T>["showSelected"];
  onCreate: RemoteSelectProps<T>["onCreate"];
  triggerType: RemoteSelectProps<T>["triggerType"];
  paginated: RemoteSelectProps<T>["paginated"];
  loadOnOpen?: boolean;
}

export function Search<T>({
  selectedResult,
  onSelectResult,
  onSearch,
  accessorFn,
  showSelected,
  onCreate,
  triggerType,
  paginated,
  loadOnOpen,
}: SearchProps<T>) {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [status, setStatus] = React.useState<
    "idle" | "loading" | "error" | "success"
  >("idle");
  const [data, setData] = React.useState<T[]>([]);
  const [hasMore, setHasMore] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  // Offset of the next page to load and the query it belongs to. Kept in refs so
  // the scroll handler always reads the latest values without re-subscribing.
  const offsetRef = React.useRef(0);
  const lastQueryRef = React.useRef("");

  const handleSelectResult = (value: T | undefined) => {
    onSelectResult(value);
  };

  const handleOnSearch = React.useCallback(
    async (value: string) => {
      lastQueryRef.current = value;
      offsetRef.current = 0;
      const result = await onSearch(value, setStatus, 0);
      setData(result);
      offsetRef.current = result.length;
      setHasMore(Boolean(paginated) && result.length > 0);
    },
    [onSearch, paginated],
  );

  const handleLoadMore = React.useCallback(async () => {
    if (!paginated || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const result = await onSearch(
        lastQueryRef.current,
        setStatus,
        offsetRef.current,
      );
      offsetRef.current += result.length;
      setData((prev) => [...prev, ...result]);
      setHasMore(result.length > 0);
    } finally {
      setLoadingMore(false);
    }
  }, [onSearch, paginated, loadingMore, hasMore]);

  const handleScroll = React.useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!paginated) return;
      const el = e.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
        handleLoadMore();
      }
    },
    [paginated, handleLoadMore],
  );

  // The list is mounted when the popover opens, so this is "on open".
  React.useEffect(() => {
    if (loadOnOpen) void handleOnSearch("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnChange = React.useCallback(
    _.debounce((value: string) => {
      if (triggerType === "change") {
        handleOnSearch(value);
      }
    }, 500),
    [handleOnSearch, triggerType],
  );

  return (
    <Command
      shouldFilter={false}
      className="h-auto rounded-lg border border-b-0 shadow-md"
    >
      <CommandInput
        value={searchQuery}
        onValueChange={(value) => {
          setSearchQuery(value);
          handleOnChange(value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && e.shiftKey && triggerType === "keyboard") {
            e.preventDefault();
            handleOnSearch(searchQuery);
          }
        }}
        placeholder="Search..."
      />
      <SearchResults
        query={searchQuery}
        status={status}
        data={
          !selectedResult ||
          _.isEmpty(accessorFn(selectedResult)!.id) ||
          !showSelected
            ? data
            : [selectedResult, ...data]
        }
        selectedResult={selectedResult}
        onSelectResult={handleSelectResult}
        accessorFn={accessorFn}
        onCreate={onCreate}
        triggerType={triggerType}
        onScroll={handleScroll}
        loadingMore={loadingMore}
      />
    </Command>
  );
}

interface SearchResultsProps<T> {
  query: string;
  status: string;
  data: T[];
  selectedResult: SearchProps<T>["selectedResult"];
  onSelectResult: SearchProps<T>["onSelectResult"];
  accessorFn: SearchProps<T>["accessorFn"];
  onCreate: SearchProps<T>["onCreate"];
  triggerType: SearchProps<T>["triggerType"];
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
  loadingMore?: boolean;
}

function SearchResults<T>({
  query,
  selectedResult,
  onSelectResult,
  status,
  data,
  accessorFn,
  onCreate,
  triggerType,
  onScroll,
  loadingMore,
}: SearchResultsProps<T>) {
  return (
    <CommandList onScroll={onScroll}>
      <CommandGroup>
        {/* TODO: these should have proper loading aria */}
        {status === "pending" && (
          <div className="p-4 text-sm">Searching...</div>
        )}
        {status === "idle" &&
          !data?.length &&
          (query === "" ? (
            <CommandEmpty className="p-2 justify-center">
              Type to search for items
            </CommandEmpty>
          ) : (
            <CommandEmpty className="p-2 justify-center">
              {triggerType === "keyboard" && (
                <span>
                  Press <kbd>Shift + Enter</kbd> to load results
                </span>
              )}
              {triggerType === "change" && (
                <span>Start typing to load results</span>
              )}
            </CommandEmpty>
          ))}
        {status === "success" && !data?.length && (
          <CommandEmpty className="p-2 justify-center flex flex-col">
            No results found
          </CommandEmpty>
        )}
        {status === "error" && (
          <div className="p-4 text-sm">Something went wrong</div>
        )}

        {data?.map((option, index) => {
          return (
            <CommandItem
              id={accessorFn(option)!!.name}
              key={`${accessorFn(option)!.id}-${index}`}
              onSelect={() => {
                if (selectedResult === option) {
                  onSelectResult(undefined);
                } else {
                  onSelectResult(option);
                }
              }}
              value={accessorFn(option)!.name}
            >
              <Check
                className={cn(
                  "mr-2 h-4 w-4",
                  selectedResult &&
                    accessorFn(selectedResult)!.id === accessorFn(option)!.id
                    ? "opacity-100"
                    : "opacity-0",
                )}
              />
              <div className="flex flex-row space-x-4 items-center">
                <div>
                  {accessorFn(option)!?.iconUrl && (
                    <img className="w-8" src={accessorFn(option)!.iconUrl} />
                  )}
                </div>
                <div>{accessorFn(option)!.name}</div>
                {accessorFn(option)!.number && (
                  <span className="ml-auto flex-1 h-4 font-mono text-xs">
                    {accessorFn(option)!.number}
                  </span>
                )}
              </div>
            </CommandItem>
          );
        })}
        {loadingMore && (
          <div className="p-2 text-center text-xs text-muted-foreground">
            Loading more...
          </div>
        )}
        {query.length > 0 && onCreate && (
          <div className="flex flex-row justify-end">
            <Button
              variant={"ghost"}
              onClick={() => {
                const created = onCreate(query);
                onSelectResult(created);
              }}
            >
              <PlusIcon />
              Create{" "}
            </Button>
          </div>
        )}
      </CommandGroup>
    </CommandList>
  );
}
